/**
 * 可见性判据的**唯一实现**（侧栏 / 模块功能地图 / 路由守卫共用）。
 *
 * 为什么单独成一个模块：这三处曾各写一份 `canSee`（RegistrySidebar、ModuleMapModal、App 守卫），
 * 判据一改就要改三处，漏一处即出现「菜单看不见但能深链进」或反过来的漂移。
 *
 * 判据两条：**权限码**（主）与**账号能力**（`requiresAgent`：是否绑定坐席档案，如坐席工作台）。
 * 角色不是判据，而是权限码的打包（`seed_rbac.go` 的 role→perms）。
 * 路由守卫额外接受角色白名单，但那只是「未被授予权限码的自定义角色」的兜底通道，见 `routeAllowed`。
 */

/** 所需权限码：单个码 / 「任一命中」码组 / null=不设权限（所有登录角色可见的公共页） */
export type PermissionCode = string | null | undefined;
export type RequiredPermission = string | string[] | null | undefined;

/** 归一成码数组（空数组 = 不设权限要求） */
export function permissionCodes(perm: RequiredPermission): string[] {
  if (!perm) return [];
  return Array.isArray(perm) ? perm.filter(Boolean) : [perm];
}

/** 权限码是否登记（登记了才谈得上「命中」；未登记=公共） */
export function hasPermissionRule(perm: RequiredPermission): boolean {
  return permissionCodes(perm).length > 0;
}

/**
 * 菜单 / 功能条目可见性：未登记权限码=公共；admin 恒放行；其余按「任一码命中」。
 * 角色白名单在此**不参与**判定——菜单只有一套判据，避免「角色允许但权限码没有」的口子。
 */
export function canSeePermission(
  perm: RequiredPermission,
  role: string | undefined,
  perms: Set<string> | string[],
): boolean {
  const codes = permissionCodes(perm);
  if (codes.length === 0) return true;
  if (role === 'admin') return true;
  const set = perms instanceof Set ? perms : new Set(perms);
  return codes.some((c) => set.has(c));
}

/** 条目可见性上下文：与角色/权限码无关的账号能力，由调用方从会话数据取值 */
export interface EntryContext {
  /**
   * 当前账号是否绑定坐席档案（`agent.user_id`）。未加载时按 false 处理——
   * 宁可晚一拍出现，也不要先显示再消失（`requiresAgent` 条目 + admin 账号的表现）。
   */
  agentBound?: boolean;
}

/** 带角色白名单的条目（锚点、页内 Tab、搜索条目）可见性：给定 roles 时按角色，否则按权限码 */
export function canSeeEntry(
  entry: { permission?: RequiredPermission; roles?: string[]; requiresAgent?: boolean },
  role: string | undefined,
  perms: Set<string> | string[],
  ctx?: EntryContext,
): boolean {
  // 账号能力条件优先于角色与权限码：它问的是「这个账号有没有坐席档案」，admin 也不豁免
  if (entry.requiresAgent && !ctx?.agentBound) return false;
  if (entry.roles) return role !== undefined && entry.roles.includes(role);
  return canSeePermission(entry.permission, role, perms);
}

/** 路由守卫登记项 */
export interface RouteGuard {
  prefix: string;
  roles: string[];
  perms?: string[];
  kbAccess?: boolean;
}

/**
 * 路由放行判定。三条并集，任一成立即放行：
 * 1. admin 恒放行（与 `usePerm` / 后端 `httpx.HasPerm` 同口径）；
 * 2. 角色在白名单内；
 * 3. **登记了权限码**且命中其一 —— 给「只被授予权限码的自定义角色」留的通道。
 *
 * 第 3 条必须带 `hasPermissionRule` 前置：`usePerm([])` 恒为 true，
 * 早期写成 `!roleOk && !permHit` 会让「未登记权限码的前缀」对全角色放行（`/system`、`/platform`
 * 这类 admin-only 前缀因此形同虚设）——这是本次修复的核心缺陷，勿回退成裸取反。
 */
export function routeAllowed(
  guard: RouteGuard | undefined,
  ctx: { role: string; permHit: boolean; hasKBRole: boolean },
): boolean {
  if (!guard) return true;
  if (ctx.role === 'admin') return true;
  if (guard.roles.includes(ctx.role)) return true;
  if (hasPermissionRule(guard.perms) && ctx.permHit) return true;
  if (guard.kbAccess && ctx.hasKBRole) return true;
  return false;
}
